import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { ordersAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { FileText, Download, Search, Calendar, Loader2, Eye } from 'lucide-react';
import PdfViewer from '../../components/PdfViewer';

const CustomerInvoices = () => {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfOrderId, setPdfOrderId] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [downloadDialogOrderId, setDownloadDialogOrderId] = useState(null);

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    try {
      const data = await ordersAPI.getAll();
      setOrders(data.filter(o => o.payment_status === 'paid'));
    } catch {
      toast({ title: 'Error', description: 'Failed to load invoices', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleView = async (orderId) => {
    try {
      setInvoiceLoading(true);
      const dataUrl = await ordersAPI.viewInvoice(orderId);
      setPdfUrl(dataUrl);
      setPdfOrderId(orderId);
    } catch {
      toast({ title: 'Error', description: 'Failed to open invoice', variant: 'destructive' });
    } finally { setInvoiceLoading(false); }
  };

  const closePdfViewer = () => { setPdfUrl(null); setPdfOrderId(null); };

  const getInvoiceDirectUrl = (orderId) => {
    const token = localStorage.getItem('at1s_token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/invoice?token=${token}`;
  };

  const filtered = orders.filter(o => {
    if (search) {
      const q = search.toLowerCase();
      if (!(o.invoice_number || '').toLowerCase().includes(q) &&
          !(o.id || '').toLowerCase().includes(q)) return false;
    }
    if (startDate && o.created_at < startDate) return false;
    if (endDate && o.created_at > endDate + 'T23:59:59') return false;
    return true;
  });

  const totalAmount = filtered.reduce((s, o) => s + (o.total || 0), 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="customer-invoices-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Invoices</h1>
          <p className="text-sm text-gray-500 mt-1">View and download your payment invoices</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-5 pb-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="total-invoices-count">{filtered.length}</p>
                <p className="text-xs text-gray-500">Total Invoices</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Download className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="total-invoices-amount">${totalAmount.toFixed(2)}</p>
                <p className="text-xs text-gray-500">Total Amount</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-gray-500 mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Invoice # or Order ID" value={search} onChange={e => setSearch(e.target.value)}
                className="pl-9" data-testid="invoice-search-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">From</label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              data-testid="invoice-start-date" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">To</label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              data-testid="invoice-end-date" />
          </div>
          <Button variant="outline" onClick={() => { setSearch(''); setStartDate(''); setEndDate(''); }}
            data-testid="invoice-clear-filters">
            Clear
          </Button>
        </div>

        {/* Invoice Table */}
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No invoices found</p>
              <p className="text-sm text-gray-400">Invoices appear here after successful payments</p>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="hidden md:grid grid-cols-6 gap-4 px-5 py-3 bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider border-b">
              <div>Invoice #</div><div>Order ID</div><div>Date</div><div className="text-right">Amount</div><div>Status</div><div></div>
            </div>
            <div className="divide-y">
              {filtered.map(order => (
                <div key={order.id} className="grid grid-cols-1 md:grid-cols-6 gap-2 md:gap-4 px-5 py-4 items-center hover:bg-slate-50 transition-colors"
                  data-testid={`invoice-row-${order.id}`}>
                  <div className="font-mono text-sm font-semibold text-gray-900" data-testid={`invoice-number-${order.id}`}>
                    {order.invoice_number || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600 font-mono">{order.id}</div>
                  <div className="text-sm text-gray-600">
                    <Calendar className="inline h-3.5 w-3.5 mr-1 text-gray-400" />
                    {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                  </div>
                  <div className="text-sm font-bold text-gray-900 md:text-right">${(order.total || 0).toFixed(2)}</div>
                  <div>
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Paid</Badge>
                  </div>
                  <div className="md:text-right flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => handleView(order.id)}
                      disabled={invoiceLoading}
                      data-testid={`view-invoice-${order.id}`}>
                      <Eye className="h-4 w-4 mr-1" /> View
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDownloadDialogOrderId(order.id)}
                      data-testid={`download-invoice-${order.id}`}>
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* PDF Viewer Dialog */}
      <Dialog open={!!pdfUrl} onOpenChange={closePdfViewer}>
        <DialogContent className="max-w-4xl p-0 pb-2">
          <DialogHeader className="px-6 pt-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-teal-600" /> Invoice - {pdfOrderId}
            </DialogTitle>
            <DialogDescription>Invoice preview</DialogDescription>
          </DialogHeader>
          {pdfUrl && (
            <PdfViewer
              dataUrl={pdfUrl}
              orderId={pdfOrderId}
              onDownload={() => { closePdfViewer(); setDownloadDialogOrderId(pdfOrderId); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Download Dialog (Edge-compatible direct link) */}
      <Dialog open={!!downloadDialogOrderId} onOpenChange={() => setDownloadDialogOrderId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-teal-600" /> Download Invoice
            </DialogTitle>
            <DialogDescription>
              Use the link below to download or open the invoice PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-5 text-center">
              <a
                href={downloadDialogOrderId ? getInvoiceDirectUrl(downloadDialogOrderId) : '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg font-bold text-teal-700 underline hover:text-teal-900 inline-flex items-center gap-2"
                data-testid="invoice-direct-download-link"
              >
                <Download className="h-5 w-5" />
                Invoice-{downloadDialogOrderId}.pdf
              </a>
              <p className="text-xs text-slate-500 mt-3">
                Click the link above to open the invoice. If blocked, <strong>right-click</strong> the link and select <strong>"Save link as..."</strong>
              </p>
            </div>
            <Button variant="outline" className="w-full" onClick={() => setDownloadDialogOrderId(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CustomerInvoices;
